import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject, EMPTY } from "rxjs";
import {
  switchMap,
  tap,
  catchError,
  finalize,
  distinctUntilChanged,
  map,
} from "rxjs/operators";

import { MindCatalogResponse } from "../../models/mind-catalog.model";
import { MindCatalogService } from "../../services/mind-catalog.service";
import { UtmPreservationService } from "../../services/utm-preservation.service";
import { LocationFacade } from "../location/location.facade";
import { unformatCnpj } from "../../utils/cnpj.util";

@Injectable({ providedIn: 'root' })
export class MindCatalogStore {

  private readonly catalogSubject =
    new BehaviorSubject<MindCatalogResponse | null>(null);

  catalog$ = this.catalogSubject.asObservable();

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  private readonly locationFacade = inject(LocationFacade);
  private readonly utmPreservationService = inject(UtmPreservationService);
  private readonly loadRequest$ = new Subject<{
    cep: string;
    number: string;
    cnpj?: string;
  }>();
  private mindService = inject(MindCatalogService);

  constructor() {
    this.locationFacade.location$.subscribe(loc => {
      if (!loc) this.clearCatalog();
    });

    this.loadRequest$
      .pipe(
        map(params => ({
          ...params,
          dedupeKey: this.catalogRequestDedupeKey(
            params.cep,
            params.number,
            params.cnpj
          ),
        })),
        distinctUntilChanged((a, b) => a.dedupeKey === b.dedupeKey),
        switchMap(({ cep, number, cnpj }) => {
          this.loadingSubject.next(true);
          return this.buildCatalog$(cep, number, cnpj).pipe(
            tap(resp => {
              this.catalogSubject.next(resp);
              if (resp?.context?.regionId != null) {
                this.locationFacade.setRegionId(String(resp.context.regionId));
              }
            }),
            catchError(err => {
              console.error('MIND STORE', err);
              this.catalogSubject.next(null);
              return EMPTY;
            }),
            finalize(() => this.loadingSubject.next(false))
          );
        })
      )
      .subscribe();
  }

  clearCatalog(): void {
    this.catalogSubject.next(null);
  }

  loadCatalog(cep: string, number: string, cnpj?: string): void {
    this.loadRequest$.next({ cep, number, cnpj });
  }

  private catalogRequestDedupeKey(
    cep: string,
    number: string,
    cnpj?: string
  ): string {
    const cleanCep = (cep || '').replace(/\D/g, '');
    const num = (number || '').trim();
    const cleanCnpj = unformatCnpj(cnpj);
    const cnpjKey = cleanCnpj.length === 14 ? cleanCnpj : '';
    let slug = '';
    if (this.utmPreservationService.isUtmEnabled()) {
      slug = (
        this.utmPreservationService.preservedUtmParams?.['utm_source'] ?? ''
      ).trim();
    }
    return `${cleanCep}|${num}|${cnpjKey}|${slug}`;
  }

  private buildCatalog$(
    cep: string,
    number: string,
    cnpj?: string
  ) {
    return cnpj && unformatCnpj(cnpj).length === 14
      ? this.mindService.isAvailable(cnpj).pipe(
          switchMap(resp => {
            const isAvailable =
              typeof resp === 'boolean'
                ? resp
                : (resp?.data?.isAvailable ?? resp?.isAvailable ?? false);
            if (isAvailable !== true) {
              throw new Error('CNPJ não disponível para o catálogo');
            }
            return this.mindService.getCatalog(cep, number);
          })
        )
      : this.mindService.getCatalog(cep, number);
  }

  get snapshot(): MindCatalogResponse | null {
    return this.catalogSubject.value;
  }
}
